<template>
    <div class="map-item-filter">
        <div class="col-sm-12 nopads vehicle-list">
            <div class="row">
                <div class="col-2 nopads" />
                <div class="col-8 nopads">
                    <span class="vehicle-list-title ml-2">{{ title }}</span>
                </div>
                <div class="col-2 nopads">
                    <div
                        class="vehicle-list-caret"
                        @click.stop="close"
                    >
                        <font-awesome-icon icon="times-circle" />
                    </div>
                </div>
            </div>

            <div class="col-sm-12 nopads vehicle-list-container">
                <div
                    v-if="roadGeometries.length > 0"
                    class="col-12 nopads text-center"
                >
                    <span
                        class="text-light px-2 pb-2 measurement-text"
                        style="font-size: 1.4em"
                    > {{ roadLength }} m</span>
                </div>
                <div class="d-flex flex-wrap justify-content-center px-2 pt-2">
                    <button
                        v-if="roadGeometries.length > 0 && showMakeNewTrafficControlButton"
                        class="btn btn-md btn-outline-info light me-2 mb-2"
                        style="background-color: #ffffff"
                        @click.stop="createNewTrafficControlItem"
                    >
                        <span class="map-button-text">{{ $t('map.new_traffic_control_entity') }}</span>
                    </button>

                    <button
                        v-if="roadGeometries.length > 0 && !hideMakeNewObservationButton"
                        class="btn btn-md btn-primary me-2 mb-2"
                        @click.stop="confirmSelection"
                    >
                        <span class="map-button-text">{{ buttonText }}</span>
                    </button>

                    <button
                        class="btn btn-md btn-secondary me-2 mb-2"
                        :disabled="roadGeometries.length < 1"
                        @click.stop="undo"
                    >
                        <span class="map-button-text">{{ $t('common.undo') }}</span>
                    </button>

                    <button
                        class="btn btn-md btn-danger mb-2"
                        @click.stop="reset"
                    >
                        <span class="map-button-text">{{ $t('common.clear') }}</span>
                    </button>
                </div>
            </div>
        </div>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {timeUtils} from '../mixins/TimeUtils'
import {mapHelper} from '../mixins/MapMixin'
import {restApi} from '../mixins/RestApiMixin'
import {getDistance} from 'geolib'
import {inject} from "vue";


export default {
    name: 'MeasureRoadMap',
    mixins: [timeUtils, mapHelper, restApi],
    props: {
        map: {
            type: Object,
            default: null
        },
        hideMakeNewObservationButton: {
            type: Boolean,
            default: false
        },
        showMakeNewTrafficControlButton: {
            type: Boolean,
            default: false
        },
        infoVisible: {
            type: Boolean,
            default: true
        },
        confirmText: {
            type: String,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            points: [],
            roadGeometries: [],
            roadLength: 0,
            buttonText: null
        }
    },

    computed: {
        title() {
            if (this.roadGeometries.length === 0) {
                return this.$t('map.measure_road_set_start')
            }
            if (this.roadGeometries.length > 0) {
                return this.$t('map.measure_road_set_end')
            }
            if (this.roadGeometries.length > 0) {
                return this.$t('map.measure_road_length')
            }
            return '';
        }
    },
    mounted() {
        if (!this.confirmText) {
            this.buttonText = this.$t('map.create_observation')
        } else {
            this.buttonText = this.confirmText
        }
    },

    methods: {
        close() {
            this.reset()
            this.$emit('close')
        },

        reset() {
            if (this.map) {
                try {
                    this.map.removeMapMarkerByType(this.MEASURE_ROAD)
                    this.map.removePolylinesByType(this.MEASURE_ROAD)
                } catch (error) {
                    // Silent error catch and fail
                }
            }

            this.roadGeometries = []
            this.points = []
        },

        undo() {
            if (this.points.length > 0) {
                if (this.roadGeometries.length > 0) {
                    this.map.removePolyline(this.roadGeometries.length-1, this.MEASURE_ROAD)
                    this.roadGeometries.pop()
                }
                // Remove last point
                this.map.removeMapMarker(this.points.length, this.MEASURE_ROAD)
                this.points.pop()

                this.calculateRoadLength()
                // Change remaining last point to draggable
                if (this.points.length > 0) {
                    let icon = this.map.getMarkerStore().getNeutralCircleIcon(this.map.getMapType() === 'LEAFLET')
                    let point = this.points[this.points.length - 1]
                    this.map.showMapMarker(this.points.length, this.MEASURE_ROAD, point.lat, point.lng, icon, true)
                }
            }
        },

        addMeasurePoint(coord) {
            if (coord.id) {
                // Existing point moved
                this.points[coord.id-1] = coord.latlng
            } else {
                this.points.push(coord)
                this.addPoint(coord, this.points.length)
            }
            if (this.points.length > 1) {
                let lastPointIndex = this.points.length-1
                if (coord.id) {
                    // Moving last point
                    this.map.removePolyline(this.roadGeometries.length-1, this.MEASURE_ROAD)
                    this.roadGeometries.pop()
                }
                if (this.points.length === 2) {
                    this.fetchRoad(this.points[0], this.points[1])
                } else {
                    this.fetchRoad(this.points[lastPointIndex], this.points[lastPointIndex-1])
                }
            }
        },

        fetchRoad(startPoint, endPoint) {
            this.loading = true
            this.restFetchParams(this.roadGeometryPointsUrl, {
                startLat: startPoint.lat,
                startLon: startPoint.lng,
                endLat: endPoint.lat,
                endLon: endPoint.lng
            }, this.handleResponse, this.onError)
        },

        handleResponse: function (response) {
            this.loading = false
            if (response && response.data && response.data.geometry) {
                if (response.data.geometry.length > 1) {
                    this.roadGeometries.push(response.data)
                } else {
                    let start = this.points[this.points.length-2]
                    let end = this.points[this.points.length-1]
                    // Use points as geometry
                    this.roadGeometries.push({
                        geometry: [
                            {lat: start.lat, lon: start.lng},
                            {lat: end.lat, lon: end.lng},
                        ],
                        properties: []
                    })
                }
                this.fillGeometryGaps()
                this.calculateRoadLength()
                this.drawLastRoad()
            }
        },

        onError() {
            this.showAlert(this.$t('map.error_road_geometry'))
            this.loading = false
            this.reset()
        },

        fillGeometryGaps() {
            if (this.roadGeometries.length > 1) {
                let lineA = this.roadGeometries[this.roadGeometries.length-1]
                let lineB = this.roadGeometries[this.roadGeometries.length-2]
                let lineAStart = lineA.geometry[0]
                let lineAEnd = lineA.geometry[lineA.geometry.length-1]
                let lineBStart = lineB.geometry[0]
                let lineBEnd = lineB.geometry[lineB.geometry.length-1]
                // Connect the nearest points
                let aStartTobStart = getDistance(lineAStart, lineBStart)
                let aEndTobStart = getDistance(lineAEnd, lineBStart)
                let aStartTobEnd = getDistance(lineAStart, lineBEnd)
                let aEndTobEnd = getDistance(lineAEnd, lineBEnd)
                let shortest = Math.min(aStartTobStart, aEndTobStart, aStartTobEnd, aEndTobEnd)
                switch (shortest) {
                case aStartTobStart:
                    lineA.geometry.unshift(lineB.geometry[0])
                    break;
                case aStartTobEnd:
                    lineA.geometry.unshift(lineB.geometry[lineB.geometry.length-1])
                    break;
                case aEndTobStart:
                    lineA.geometry.push(lineB.geometry[0])
                    break;
                case aEndTobEnd:
                    lineA.geometry.push(lineB.geometry[lineB.geometry.length-1])
                    break;
                }
            }
        },

        getMergedLine() {
            let mergedGeometry = []
            if (this.roadGeometries.length > 0) {
                this.roadGeometries.forEach(item => {
                    if (mergedGeometry.length < 1) {
                        mergedGeometry = item.geometry
                    } else {
                        // Find the touching point and merge geometry, if needed change geometry order
                        let start = mergedGeometry[0]
                        let end = mergedGeometry[mergedGeometry.length-1]
                        if (getDistance(start, item.geometry[0]) === 0) {
                            item.geometry.reverse()
                            mergedGeometry = [ ...item.geometry, ...mergedGeometry ]
                        } else if (getDistance(start, item.geometry[item.geometry.length-1]) === 0) {
                            mergedGeometry = [...item.geometry, ...mergedGeometry]
                        } else if (getDistance(end, item.geometry[item.geometry.length-1]) === 0) {
                            item.geometry.reverse()
                            mergedGeometry = [...mergedGeometry, ...item.geometry]
                        } else if (getDistance(end, item.geometry[0]) === 0) {
                            mergedGeometry = [...mergedGeometry, ...item.geometry]
                        }
                    }
                })
            }
            return mergedGeometry
        },

        calculateRoadLength() {
            let totalLength = 0
            let lastPoint = undefined
            this.getMergedLine().forEach(point => {
                if (lastPoint) {
                    totalLength += getDistance(lastPoint, point)
                }
                lastPoint = point
            })
            this.roadLength = totalLength
        },

        addPoint(point, id) {
            // Re-draw previous points and set then non-draggable
            if (id > 1) {
                let prevPoint = this.points[id-2]
                let staticIcon = this.map.getMarkerStore().getMeasureDistanceMarkerIcon(this.map.getMapType() === 'LEAFLET')
                this.map.removeMapMarker(id-1, this.MEASURE_ROAD)
                this.map.showMapMarker(id-1, this.MEASURE_ROAD, prevPoint.lat,
                    prevPoint.lng, staticIcon, false)
            }
            // draw new marker that is draggable
            let icon = this.map.getMarkerStore().getNeutralCircleIcon(this.map.getMapType() === 'LEAFLET')
            this.map.showMapMarker(id, this.MEASURE_ROAD, point.lat,
                point.lng, icon, true)
        },

        drawLastRoad() {
            // Draw the last road on the map
            let lastRoad = this.roadGeometries[this.roadGeometries.length-1]
            var xyLine = lastRoad.geometry.map(point => {
                return {x: point.lon, y: point.lat}
            })
            this.map.drawPolyLine(this.roadGeometries.length-1, this.MEASURE_ROAD, xyLine, '#404041', false, false)
        },

        confirmSelection() {
            if (this.roadGeometries.length === 1) {
                this.$emit('confirmSelection', this.roadGeometries[0].geometry, this.roadGeometries[0].properties)
            } else {
                this.$emit('confirmSelection', this.getMergedLine(), [])
            }
        },

        createNewTrafficControlItem() {
            if (this.roadGeometries.length === 1) {
                this.$emit('createNewTrafficControlItem', this.roadGeometries[0].geometry, this.roadGeometries[0].properties)
            } else {
                this.$emit('createNewTrafficControlItem', this.getMergedLine(), [])
            }
        }

    }
}
</script>
